import React, { useEffect, useMemo, useState } from 'react';
import { AdminSidebar } from '../AdminDashboardPage';
import AdminHeader from '../AdminHeader';
import { getAllOrders } from '../../../services/orderService';
import { getTextileProducts } from '../../../services/productService';
import formatPrice from '../../../utils/formatPrice';
import { Pie, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from 'chart.js';
import '../../../css/productReport.css';

import {
  Package,
  ShoppingBag,
  TrendingUp,
  AlertTriangle,
  PackageX,
  Archive,
  FileSpreadsheet,
  Filter,
  Calendar,
} from 'lucide-react';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

// Match the dashboard's chart typography + tooltip
ChartJS.defaults.font.family = "'Figtree', 'Segoe UI', system-ui, sans-serif";
ChartJS.defaults.font.size = 12;
ChartJS.defaults.color = '#8f8178';
Object.assign(ChartJS.defaults.plugins.tooltip, {
  backgroundColor: '#211915',
  titleColor: '#f5ede6',
  bodyColor: '#f5ede6',
  padding: 10,
  cornerRadius: 8,
  displayColors: false,
});

const PALETTE = ['#f4b6c4', '#4a6382', '#2e6a4f', '#c48a2c', '#8a6a9e', '#3d8796', '#d9466f', '#8f8178'];
const GRID_COLOR = '#f0ebe6';
const AXIS_COLOR = '#e7dfd8';

const pieLegend = {
  position: 'bottom',
  labels: { usePointStyle: true, pointStyle: 'rectRounded', boxWidth: 8, boxHeight: 8, padding: 16, color: '#4a3d36' },
};
const LOW_STOCK_THRESHOLD = 5;

const toISODate = (d) => d.toISOString().slice(0, 10);

const PRESETS = [
  { key: '7d', label: '7 days' },
  { key: '30d', label: '30 days' },
  { key: 'month', label: 'This month' },
  { key: 'all', label: 'All time' },
  { key: 'custom', label: 'Custom' },
];

const rangeForPreset = (preset) => {
  const today = new Date();
  const to = toISODate(today);
  if (preset === '7d') {
    const from = new Date(today); from.setDate(from.getDate() - 6);
    return { from: toISODate(from), to };
  }
  if (preset === '30d') {
    const from = new Date(today); from.setDate(from.getDate() - 29);
    return { from: toISODate(from), to };
  }
  if (preset === 'month') {
    const from = new Date(today.getFullYear(), today.getMonth(), 1);
    return { from: toISODate(from), to };
  }
  if (preset === 'all') return { from: '2000-01-01', to };
  return { from: to, to };
};

// stock is already aggregated server-side onto product.stock (see
// listTextileProducts), no need to sum variants ourselves

const ProductReportPage = () => {
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [productsError, setProductsError] = useState('');
  const [preset, setPreset] = useState('30d');
  const [range, setRange] = useState(rangeForPreset('30d'));

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      setProductsError('');
      try {
        const orderList = await getAllOrders({});
        setOrders(Array.isArray(orderList) ? orderList : []);
      } catch (err) {
        console.error('Error loading orders for product report:', err);
        setError('Could not load sales data.');
        setOrders([]);
      }
      try {
        const res = await getTextileProducts({ limit: 1000 });
        setProducts(Array.isArray(res?.products) ? res.products : []);
      } catch (err) {
        console.error('Error loading product catalog:', err);
        setProductsError('Could not load the product catalog — stock and category data will be empty.');
        setProducts([]);
      }
      setLoading(false);
    };
    load();
  }, []);

  const applyPreset = (key) => {
    setPreset(key);
    if (key !== 'custom') setRange(rangeForPreset(key));
  };

  const handleCustomDate = (field, value) => {
    setPreset('custom');
    setRange((prev) => ({ ...prev, [field]: value }));
  };

  const filteredOrders = useMemo(() => {
    const fromTime = new Date(range.from).setHours(0, 0, 0, 0);
    const toTime = new Date(range.to).setHours(23, 59, 59, 999);
    return orders.filter((o) => {
      const t = new Date(o.createdAt).getTime();
      return t >= fromTime && t <= toTime && !['Cancelled', 'Rejected', 'Returned'].includes(o.status);
    });
  }, [orders, range]);

  const stats = useMemo(() => {
    // sales aggregation from order items
    const salesMap = {};
    filteredOrders.forEach((o) => {
      (o.orderItems || []).forEach((it) => {
        if (!salesMap[it.name]) salesMap[it.name] = { totalSold: 0, revenue: 0 };
        salesMap[it.name].totalSold += it.qty || 0;
        salesMap[it.name].revenue += (it.price || 0) * (it.qty || 0);
      });
    });

    const soldEntries = Object.entries(salesMap).map(([name, v]) => ({ name, ...v }));
    const topSellers = [...soldEntries].sort((a, b) => b.totalSold - a.totalSold).slice(0, 10);
    const lowSellers = [...soldEntries].sort((a, b) => a.totalSold - b.totalSold).slice(0, 10);

    const unitsSold = soldEntries.reduce((sum, p) => sum + p.totalSold, 0);
    const revenue = soldEntries.reduce((sum, p) => sum + p.revenue, 0);

    // catalog-side stats
    const totalProducts = products.length;
    const stockLevels = products.map((p) => ({
      name: p.name,
      category: p.subCategory || p.category || 'Uncategorized',
      stock: p.stock || 0,
    }));
    const outOfStock = stockLevels.filter((p) => p.stock === 0);
    const lowStock = stockLevels.filter((p) => p.stock > 0 && p.stock <= LOW_STOCK_THRESHOLD);
    const inStock = stockLevels.filter((p) => p.stock > LOW_STOCK_THRESHOLD);

    const soldNames = new Set(Object.keys(salesMap));
    const neverSold = products
      .filter((p) => !soldNames.has(p.name))
      .map((p) => ({ name: p.name, category: p.subCategory || p.category || 'Uncategorized', stock: p.stock || 0 }));

    const categoryMap = {};
    products.forEach((p) => {
      const cat = p.subCategory || p.category || 'Uncategorized';
      categoryMap[cat] = (categoryMap[cat] || 0) + 1;
    });

    return {
      topSellers, lowSellers, unitsSold, revenue,
      totalProducts, outOfStock, lowStock, inStock, neverSold,
      stockLevels: stockLevels.sort((a, b) => a.stock - b.stock),
      categoryBreakdown: Object.entries(categoryMap).map(([category, count]) => ({ category, count })),
    };
  }, [filteredOrders, products]);

  const topSellersData = {
    labels: stats.topSellers.map((p) => p.name),
    datasets: [{
      label: 'Units sold',
      data: stats.topSellers.map((p) => p.totalSold),
      backgroundColor: '#f4b6c4',
      hoverBackgroundColor: '#e38aa2',
      borderWidth: 0,
      borderRadius: 6,
      maxBarThickness: 34,
    }],
  };

  const lowSellersData = {
    labels: stats.lowSellers.map((p) => p.name),
    datasets: [{
      label: 'Units sold',
      data: stats.lowSellers.map((p) => p.totalSold),
      backgroundColor: '#8f8178',
      hoverBackgroundColor: '#5f524a',
      borderWidth: 0,
      borderRadius: 6,
      maxBarThickness: 34,
    }],
  };

  const categoryData = {
    labels: stats.categoryBreakdown.map((c) => c.category),
    datasets: [{
      data: stats.categoryBreakdown.map((c) => c.count),
      backgroundColor: PALETTE,
      borderColor: '#ffffff',
      borderWidth: 3,
      hoverOffset: 4,
    }],
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      y: { beginAtZero: true, ticks: { precision: 0 }, grid: { color: GRID_COLOR }, border: { display: false } },
      x: { grid: { display: false }, border: { color: AXIS_COLOR } },
    },
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: pieLegend },
  };

  const handleExportCsv = () => {
    const rows = [
      ['Product', 'Category', 'Stock', 'Units sold (range)', 'Revenue (range)'],
      ...stats.stockLevels.map((p) => {
        const sold = [...stats.topSellers, ...stats.lowSellers].find((s) => s.name === p.name);
        return [p.name, p.category, p.stock, sold?.totalSold || 0, sold?.revenue || 0];
      }),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `product_report_${range.from}_to_${range.to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const kpis = [
    { key: 'catalog', label: 'Catalog size', value: stats.totalProducts, icon: Package, note: 'Products in the catalog' },
    { key: 'units', label: 'Units sold', value: stats.unitsSold, icon: ShoppingBag, note: 'In selected period' },
    { key: 'revenue', label: 'Revenue', value: formatPrice(stats.revenue), icon: TrendingUp, note: 'From product sales in period' },
    {
      key: 'low',
      label: `Low stock (≤${LOW_STOCK_THRESHOLD})`,
      value: stats.lowStock.length,
      icon: AlertTriangle,
      tone: stats.lowStock.length > 0 ? 'warn' : null,
      note: 'Running low, restock soon',
    },
    {
      key: 'out',
      label: 'Out of stock',
      value: stats.outOfStock.length,
      icon: PackageX,
      tone: stats.outOfStock.length > 0 ? 'danger' : null,
      note: 'Unavailable to customers',
    },
    { key: 'never', label: 'Never sold (range)', value: stats.neverSold.length, icon: Archive, note: 'No sales in selected period' },
  ];

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content">
        <AdminHeader title="Product Report" />

        <div className="pdr-page">
          {/* Page heading */}
          <div className="pdr-heading">
            <div>
              <h1 className="pdr-title">Product Report</h1>
              <p className="pdr-subtitle">Best and slowest sellers, category mix and stock levels</p>
            </div>
            <div className="pdr-heading-actions">
              <button
                type="button"
                className="pdr-btn pdr-btn-primary"
                onClick={handleExportCsv}
                disabled={loading}
              >
                <FileSpreadsheet size={15} />
                <span>Export CSV</span>
              </button>
            </div>
          </div>

          {/* Filter bar */}
          <div className="pdr-filter-bar">
            <div className="pdr-filter-left">
              <span className="pdr-filter-label">
                <Filter size={13} />
                <span>Period</span>
              </span>
              <div className="pdr-presets" role="group" aria-label="Date filter presets">
                {PRESETS.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    className={`pdr-preset-btn ${preset === p.key ? 'active' : ''}`}
                    onClick={() => applyPreset(p.key)}
                    aria-pressed={preset === p.key}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="pdr-date-inputs">
              <span className="pdr-date-icon">
                <Calendar size={14} />
              </span>
              <input type="date" value={range.from} max={range.to} aria-label="From date" onChange={(e) => handleCustomDate('from', e.target.value)} />
              <span className="pdr-date-sep">to</span>
              <input type="date" value={range.to} min={range.from} aria-label="To date" onChange={(e) => handleCustomDate('to', e.target.value)} />
            </div>
          </div>

          {error && <div className="pdr-error">{error}</div>}
          {productsError && <div className="pdr-error pdr-error-warn">{productsError}</div>}

          {/* Summary cards */}
          <div className="pdr-grid">
            {kpis.map(({ key, label, value, icon: Icon, tone, note }) => (
              <div key={key} className={`pdr-kpi ${tone && !loading ? `pdr-kpi--${tone}` : ''}`}>
                <div className="pdr-kpi-top">
                  <span className="pdr-kpi-label">{label}</span>
                  <span className="pdr-kpi-icon">
                    <Icon size={16} />
                  </span>
                </div>
                <p className="pdr-kpi-value">{loading ? '—' : value}</p>
                <span className="pdr-kpi-note">{note}</span>
              </div>
            ))}
          </div>

          {/* Top / Low sellers */}
          <div className="pdr-row">
            <section className="pdr-panel">
              <header className="pdr-panel-head">
                <div>
                  <h3 className="pdr-panel-title">Top selling products</h3>
                  <p className="pdr-panel-sub">Most units sold in the selected period</p>
                </div>
                <span className="pdr-tag">Top 10</span>
              </header>
              {stats.topSellers.length === 0 && !loading ? (
                <p className="pdr-empty">No sales in this range.</p>
              ) : (
                <div className="pdr-chart">
                  <Bar data={topSellersData} options={barOptions} />
                </div>
              )}
            </section>
            <section className="pdr-panel">
              <header className="pdr-panel-head">
                <div>
                  <h3 className="pdr-panel-title">Lowest selling products</h3>
                  <p className="pdr-panel-sub">Fewest units sold among products with sales</p>
                </div>
                <span className="pdr-tag">Bottom 10</span>
              </header>
              {stats.lowSellers.length === 0 && !loading ? (
                <p className="pdr-empty">No sales in this range.</p>
              ) : (
                <div className="pdr-chart">
                  <Bar data={lowSellersData} options={barOptions} />
                </div>
              )}
            </section>
          </div>

          {/* Category mix */}
          <section className="pdr-panel">
            <header className="pdr-panel-head">
              <div>
                <h3 className="pdr-panel-title">Category mix</h3>
                <p className="pdr-panel-sub">Number of catalog products per category</p>
              </div>
            </header>
            {stats.categoryBreakdown.length === 0 && !loading ? (
              <p className="pdr-empty">No category data available.</p>
            ) : (
              <div className="pdr-chart pdr-chart--pie">
                <Pie data={categoryData} options={pieOptions} />
              </div>
            )}
          </section>

          {/* Never sold */}
          {stats.neverSold.length > 0 && (
            <section className="pdr-panel">
              <header className="pdr-panel-head">
                <div>
                  <h3 className="pdr-panel-title">Never sold in this range</h3>
                  <p className="pdr-panel-sub">
                    {stats.neverSold.length} {stats.neverSold.length === 1 ? 'product' : 'products'}
                    {stats.neverSold.length > 20 ? ' · showing first 20' : ''}
                  </p>
                </div>
              </header>
              <div className="pdr-table-wrap">
                <table className="pdr-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Category</th>
                      <th className="pdr-num">Stock</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.neverSold.slice(0, 20).map((p) => (
                      <tr key={p.name}>
                        <td className="pdr-strong">{p.name}</td>
                        <td className="pdr-muted">{p.category}</td>
                        <td className={`pdr-num ${p.stock === 0 ? 'pdr-stock-critical' : ''}`}>{p.stock}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* Full stock table */}
          <section className="pdr-panel">
            <header className="pdr-panel-head">
              <div>
                <h3 className="pdr-panel-title">Overall product stock</h3>
                <p className="pdr-panel-sub">
                  {stats.stockLevels.length} {stats.stockLevels.length === 1 ? 'product' : 'products'} · lowest stock first
                </p>
              </div>
            </header>
            {stats.stockLevels.length === 0 && !loading ? (
              <p className="pdr-empty">No product catalog data available.</p>
            ) : (
              <div className="pdr-table-wrap pdr-table-wrap--scroll">
                <table className="pdr-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Category</th>
                      <th className="pdr-num">Stock</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.stockLevels.map((p) => (
                      <tr key={p.name}>
                        <td className="pdr-strong">{p.name}</td>
                        <td className="pdr-muted">{p.category}</td>
                        <td className={`pdr-num ${p.stock === 0 ? 'pdr-stock-critical' : p.stock <= LOW_STOCK_THRESHOLD ? 'pdr-stock-low' : ''}`}>{p.stock}</td>
                        <td>
                          {p.stock === 0 ? (
                            <span className="pdr-pill pdr-pill-danger">Out of stock</span>
                          ) : p.stock <= LOW_STOCK_THRESHOLD ? (
                            <span className="pdr-pill pdr-pill-warn">Low stock</span>
                          ) : (
                            <span className="pdr-pill pdr-pill-success">In stock</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default ProductReportPage;
